import React from 'react';
import { Outlet } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { ConnectionStatusBadge } from './shared/ConnectionStatusBadge';
import { NavigationRenderedProvider } from '../contexts/NavigationContext';
import { RunnerBarProvider } from '../contexts/RunnerBarContext';
import { RunnerBar, RunnerBarSpacer } from './FieldService/RunnerBar';
import { WarnWhileCreatingEquipment } from './Equipment/WarnWhileCreatingEquipment';

interface ModuleLayoutProps {
  children?: React.ReactNode;
}

/**
 * Route-level layout that provides Navigation for an entire module.
 *
 * Wrap module routes in this layout so that:
 * - Every page automatically gets the top bar, module selector, and module nav
 * - Pages never need to import Navigation directly
 * - Duplicate Navigation renders are impossible
 *
 * Pages that use <PageLayout> for content styling (fullWidth, header, bg)
 * continue to work — PageLayout detects the context and skips its own
 * Navigation render while preserving the content wrapper.
 *
 * Supports two patterns:
 *   // As a wrapper in App.tsx (pass children):
 *   <ModuleLayout><Routes>...</Routes></ModuleLayout>
 *
 *   // As a route element (uses Outlet):
 *   { element: <ModuleLayout />, children: [...] }
 *
 * Also hosts the Field Service "runner bar" (`RunnerBarProvider` + `RunnerBar`,
 * `contexts/RunnerBarContext.tsx` / `components/FieldService/RunnerBar.tsx`) the same way it
 * hosts Navigation: rendered exactly once per route, so a page declares its bar contents via
 * `useRunnerBar()` instead of importing or rendering the bar directly. `RunnerBar` and its
 * spacer (`RunnerBarSpacer`) both render `null` whenever no page has registered a config —
 * which is every route in the other 12 modules, and any Field Service route that has not
 * adopted the bar — so this addition is inert everywhere except the four pages that opt in.
 */
export function ModuleLayout({ children }: ModuleLayoutProps) {
  return (
    <>
      <Navigation />
      {/* ISS-445 / #4602 — mounted ONCE here, not per page. Covers the silent
          cache-fallback case that no individual query can report: a read that
          succeeded from cache resolves normally and carries no staleness marker.
          Renders nothing while online. */}
      <ConnectionStatusBadge />
      {/* Mounted HERE, above every form: a background equipment create outlives the
          form that started it (both create forms close on submit). Renders nothing. */}
      <WarnWhileCreatingEquipment />
      <NavigationRenderedProvider value={true}>
        <RunnerBarProvider>
          {children ?? <Outlet />}
          {/* Reserves space for the fixed-position bar below so it never covers a page's
              last row — renders nothing (and takes no space) when the bar itself is not
              showing. Must live INSIDE the provider to know whether a config is registered. */}
          <RunnerBarSpacer />
          <RunnerBar />
        </RunnerBarProvider>
      </NavigationRenderedProvider>
    </>
  );
}
